/** @jsxImportSource @emotion/react */
import React, { useEffect, useState, useImperativeHandle, useRef } from 'react';
import useEvent from 'react-use-event-hook';
import { useTranslation } from 'react-i18next';
import { FormControl, FormControlLabel, Radio, RadioGroup } from '@mui/material';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import { tb } from 'components/layouts/Table';
import CustomTextarea from 'components/inputs/CustomTextarea';
import { AllEditorInputWrap, form, st } from 'components/inputs/CustomInput';
import CustomInputWithSearch from 'components/inputs/CustomInputWithSearch';
import { ComboBox } from 'components/selects/ComboBox';
import { getCommonCodeNames, getCommonCodes } from 'apis/admin/CommonCode';
import { Code } from 'models/common/CommonCode';
import { RequestDetail } from 'models/approves/ReferenceOpinion';
import _, { toNumber } from 'lodash';
import { getApprovalTemplate } from 'apis/admin/Approval';
import { ApprovalTemplate, aprTplNm } from 'models/admin/ApprovalTemplate';
import CrossEditor from '../../components/crosseditor/CrossEditor';
import DOMPurify from 'dompurify';
import { useLocation } from 'react-router-dom';
import CustomEditorInputWithSearch from 'components/inputs/CustomEditorInputWithSearch';
import { FileuploadBtn } from '../../components/buttons/CustomButton';
import FileUploadPopUp from '../common/components/FileUploadPopUp';
import { generateAtchFileGrId } from '../../apis/mp/MpItem';
import { useMessageBar } from '../../hooks/useMessageBar';
import { CommonUtil } from '../../utils/CommonUtil';

type Props = {
  detail: RequestDetail;
  onChange?: (name: string, value: string) => void;
  isReadOnly?: boolean;
};

const ApproveBasicInfo = ({ detail, onChange, isReadOnly = false }: Props, ref) => {
  const { t } = useTranslation();
  const [errors, setErrors] = useState<any>({});
  const [apprSecurityTypeCds, setApprSecurityTypeCds] = useState<Code[]>([
    { cmnCd: '0', cmnCdNm: String(t('com.label.일반문서', '일반문서')) },
    { cmnCd: '1', cmnCdNm: String(t('com.label.비밀문서', '비밀문서')) },
  ]);

  /*
  const [apprLineTypeCds, setApprLineTypeCds] = useState<Code[]>([
    { cmnCd: '0', cmnCdNm: String(t('com.label.순차', '순차')) },
    { cmnCd: '1', cmnCdNm: String(t('com.label.협의', '협의')) },
  ]);
  */
  const locationState = useLocation().state;
  const [code, setCode] = useState<any>();
  const [aprTplNm, setAprTplNm] = useState<any>();
  const editorRef = useRef<CrossEditor>(null);

  //[26.02.13] 임시저장 때 요청내용 많을 시 editor 불러오고 값넣어주는용
  // const [editorReady, setEditorReady] = useState(false);

  useImperativeHandle(ref, () => ({
    validate: () => {
      if (isReadOnly) return true;

      const error = {};
      if (!detail?.aprReqTitNm) {
        Object.assign(error, { aprReqTitNm: true });
      }
      if (!detail?.trnmBdyCtn) {
        Object.assign(error, { trnmBdyCtn: true });
      }

      setErrors(error);
      if (!Object.keys(error).filter((k) => error[k]).length) {
        return true;
      }
      return false;
    },
    getReqContent: () => {
      const content = DOMPurify.sanitize(editorRef.current?.editorRef.GetBodyValue());
      return content ? content : '';
    },
  }));

  useEffect(() => {
    getCommonCode();
  }, [detail]);

  // const handleEditerOnLoad = useEvent(() => {
  //   setEditorReady(true);
  // });

  // //[26.02.13] 임시저장 때 요청내용 많을 시 editor 불러오고 값넣어주는용
  // useEffect(() => {
  //   if (!editorReady) return;
  //   if (!detail.trnmBdyCtn) return;
  //   editorRef.current?.editorRef.SetBodyValue(
  //     CommonUtil.safeHtmlContents(detail.trnmBdyCtn ?? '', false)
  //   );
  // }, [editorReady, detail.trnmBdyCtn]);

  const getCommonCode = async () => {
    const aprPvnDdlnCd: Code[] = await getCommonCodeNames('APR_PVN_DDLN_CD');

    //pageId로 공통코드를 가져옴
    const aprReqPgeTpCd = await getCommonCodeNames('APR_REQ_PAGE_TP_CD');
    if (detail?.aprTplNm) {
      setAprTplNm(detail.aprTplNm);
    } else {
      // 업무화면 -> 신규 / 수정 / 삭제
      const aprPageId = aprReqPgeTpCd.filter((item) => item.cmnCd === detail.aprPageId);
      setAprTplNm(aprPageId[0]?.cmnCdNm);
    }

    setCode({
      aprPvnDdlnCd: aprPvnDdlnCd,
    });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setErrors({ ...errors, [e.target.name]: false });
    onChange && onChange(e.target.name, e.target.value);
  };

  const handleEditorChange = useEvent((e) => {
    const target = e?.editorName;
    let targetText = e.editorTarget.GetBodyValue();
    setErrors({ ...errors, [target]: false });
    // 실질적인 내용이 있는지 체크
    const doc = new DOMParser().parseFromString(targetText, 'text/html');
    const hasText = doc.body.textContent?.trim().length ?? 0;

    if (hasText === 0) targetText = '';
    onChange && onChange(target, targetText);
    editorRef.current?.editorRef.SaveBodyValue?.();
  });

  // 결재 첨부파일 추가
  const { openMessageBar } = useMessageBar();
  const [isOpenFileUploadPopUp, setOpenFileUploadPopUp] = useState<boolean>(false);
  const [fileUploadModalCondition, setFileUploadModalCondition] = useState<any>();
  const handleFileUpload = async () => {
    const downloadOnly = false;
    const fileCount = 0;
    // fileCount = Number(detail?.fileCount || 0);

    let atchFileGrId = detail?.atchFileGrId;
    if (_.isEmpty(atchFileGrId)) {
      atchFileGrId = await generateAtchFileGrId(detail?.aprReqId || '');
      if (atchFileGrId) {
        detail.atchFileGrId = atchFileGrId;
      } else {
        openMessageBar({
          type: 'error',
          content:
            t(
              'com.label.첨부파일그룹ID 생성에 실패했습니다.',
              '첨부파일그룹ID 생성에 실패했습니다.'
            ) +
            <br /> +
            t('com.label.새로고침 후 다시 시도해 주세요.', '새로고침 후 다시 시도해 주세요.'),
        });
        return;
      }
    }

    setFileUploadModalCondition({
      atchFileGrId: atchFileGrId,
      atchFileTpCd: 'APPR_REQUEST',
      tableName: 'tb_elm_apr_req_m',
      bizName: 'appr',
    });
    setOpenFileUploadPopUp(true);
  };

  return (
    <>
      <TableContainer css={tb.table}>
        <Table>
          <TableBody>
            <TableRow>
              <TableCell>{t('com.label.요청 ID', '요청 ID')}</TableCell>
              <TableCell className="w500">{detail.aprReqId}</TableCell>
              <TableCell>{t('com.label.진행상태', '진행상태')}</TableCell>
              <TableCell className="w500">{detail.aprReqProgStatNm}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>{t('com.label.작성자', '작성자')}</TableCell>
              <TableCell className="w500">{detail.aprReqUserNm}</TableCell>
              <TableCell>{t('com.label.결재요청양식', '요청양식')}</TableCell>
              <TableCell className="w500">{aprTplNm}</TableCell>
              {/*
              // TODO [24.03.22] 전자결재 시 일반문서(0) 고정으로 사용 (따로 저장하지 않음)
              <TableCell>{t('com.label.문서종류', '문서종류')}</TableCell>
              <TableCell className="w500">
                <FormControl>
                  <RadioGroup
                    css={form.radioGroup}
                    row
                    aria-labelledby="radio-buttons-group-label"
                    name="radio-buttons-group"
                  >
                    {apprSecurityTypeCds.map((o) => (
                      <FormControlLabel
                        key={o.cmnCd}
                        id={`appr_security_type_cd_${o.cmnCd}`}
                        value={o.cmnCd}
                        name={'apprSecurityType'}
                        control={<Radio />}
                        label={o.cmnCdNm}
                        checked={detail?.apprSecurityType === o.cmnCd}
                        disabled={true} // 문서종류 미사용으로 disabled 처리
                      />
                    ))}
                  </RadioGroup>
                </FormControl>
              </TableCell>*/}
            </TableRow>
            <TableRow>
              <TableCell>{t('com.label.작성일시', '작성일시')}</TableCell>
              <TableCell className="w500">{detail.dataInsDtm ?? ''}</TableCell>
              <TableCell>{t('com.label.보존년한', '보존년한')}</TableCell>
              <TableCell className="w500">
                <ComboBox
                  options={code?.aprPvnDdlnCd}
                  defaultValue={detail.aprPvnDdlnCd}
                  classNameWrap={'w450'}
                  onChange={(value: string) => {
                    onChange?.('aprPvnDdlnCd', value);
                  }}
                  readOnly={isReadOnly}
                />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <span className="dot">{t('com.label.요청 제목', '요청 제목')}</span>
              </TableCell>
              <TableCell>
                <CustomInputWithSearch
                  type="text"
                  name="aprReqTitNm"
                  placeholder={String(
                    t('com.msg.요청 제목을 입력해 주세요.', '요청 제목을 입력해 주세요.')
                  )}
                  value={detail.aprReqTitNm}
                  onChange={handleChange}
                  isError={errors?.aprReqTitNm}
                  msgError={String(
                    t('com.msg.요청 제목을 입력해 주세요.', '요청 제목을 입력해 주세요.')
                  )}
                  readOnly={isReadOnly}
                />
              </TableCell>
              <TableCell>{t('com.label.첨부', '첨부')}</TableCell>
              <TableCell>
                <FileuploadBtn onClick={handleFileUpload}>
                  <span>{Number(detail?.fileCount || 0).toLocaleString()}</span>
                </FileuploadBtn>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <span className="dot">{t('com.label.요청 내용', '요청 내용')}</span>
              </TableCell>
              <TableCell colSpan={3}>
                <CustomEditorInputWithSearch
                  editorRef={editorRef}
                  name={'trnmBdyCtn'}
                  value={detail.trnmBdyCtn}
                  isError={errors?.trnmBdyCtn}
                  msgError={String(
                    t('com.msg.요청 내용을 입력해 주세요.', '요청 내용을 입력해 주세요.')
                  )}
                  readOnly={isReadOnly}
                  onKeyActive={handleEditorChange}
                  // onLoaded={handleEditerOnLoad}
                />
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* 첨부파일 팝업 */}
      {isOpenFileUploadPopUp && (
        <FileUploadPopUp
          open={isOpenFileUploadPopUp}
          close={() => setOpenFileUploadPopUp(false)}
          // downloadOnly={fileUploadModalCondition?.downloadOnly}
          initParam={{
            atchFileGrId: fileUploadModalCondition?.atchFileGrId,
            atchFileTpCd: fileUploadModalCondition?.atchFileTpCd,
            optValCtn1: fileUploadModalCondition?.tableName,
            bizName: fileUploadModalCondition?.bizName,
          }}
          onCallback={(atchFileGrId, fileCount) => {
            console.log(fileCount);
            detail.fileCount = fileCount.toString(); //파일개수
            setOpenFileUploadPopUp(false);
          }}
        />
      )}
    </>
  );
};

export default React.forwardRef(ApproveBasicInfo);
